import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock } from 'lucide-react'
import { NOTE_COLORS } from './StickyPopup'
import { todayISO, addDaysISO, nextMondayISO, shortDate, relativeTarget, atTimeOn, sameTimeNow } from './reminderTime'
import type { ReminderColor, ReminderDraft, EntityLinkPayload } from './types'

const NOTE_ORDER: ReminderColor[] = ['yellow', 'pink', 'green', 'blue']

interface Props {
  tz: string
  defaultColor: ReminderColor
  onAdd: (draft: ReminderDraft) => void
  lockedPayload?: EntityLinkPayload
}

export function QuickAdd({ tz, defaultColor, onAdd, lockedPayload }: Props) {
  const { t } = useTranslation('reminders')
  const [text, setText] = useState('')
  const [date, setDate] = useState(() => todayISO(tz))
  const [time, setTime] = useState('') // '' = Anytime/all-day
  const [color, setColor] = useState<ReminderColor>(defaultColor)
  const [picking, setPicking] = useState(false)
  const [pickingTime, setPickingTime] = useState(false)
  useEffect(() => setColor(defaultColor), [defaultColor])

  const monISO = nextMondayISO(tz)
  const presets: [string, string][] = [
    [t('today'), todayISO(tz)],
    [t('tomorrow'), addDaysISO(1, tz)],
    [t('nextMonday', { day: Number(monISO.slice(-2)) }), monISO],
  ]
  const isPreset = presets.some(([, iso]) => iso === date)
  const isToday = date === todayISO(tz)

  const cycleColor = () => {
    const i = NOTE_ORDER.indexOf(color)
    setColor(NOTE_ORDER[(i + 1) % NOTE_ORDER.length])
  }

  const pickRelative = (mins: number) => {
    const { dueDate, dueTime } = relativeTarget(mins, tz)
    setDate(dueDate); setTime(dueTime); setPickingTime(false)
  }
  const pickAbsolute = (hhmm: string) => {
    const { dueDate, dueTime } = atTimeOn(date, hhmm)
    setDate(dueDate); setTime(dueTime); setPickingTime(false)
  }
  const pickAnytime = () => { setTime(''); setPickingTime(false) }

  const submit = () => {
    const v = text.trim()
    if (!v) return
    onAdd({
      text: v, dueDate: date, dueTime: time || null, color,
      ...(lockedPayload ? { payloadType: 'entity_link', payload: lockedPayload } : {}),
    })
    setText(''); setTime(''); setDate(todayISO(tz)); setPicking(false); setPickingTime(false)
  }

  const timeChips: Array<{ key: string; label: string; on: boolean; click: () => void }> = isToday
    ? [
        { key: 'any', label: t('anytime'), on: time === '', click: pickAnytime },
        { key: '30', label: t('in30m'), on: false, click: () => pickRelative(30) },
        { key: '60', label: t('in1h'), on: false, click: () => pickRelative(60) },
        { key: '180', label: t('in3h'), on: false, click: () => pickRelative(180) },
        { key: '240', label: t('in4h'), on: false, click: () => pickRelative(240) },
      ]
    : [
        { key: 'any', label: t('anytime'), on: time === '', click: pickAnytime },
        { key: '9', label: t('at9am'), on: time === '09:00', click: () => pickAbsolute('09:00') },
        { key: 'same', label: t('sameTime'), on: false, click: () => pickAbsolute(sameTimeNow(tz)) },
        { key: '18', label: t('at6pm'), on: time === '18:00', click: () => pickAbsolute('18:00') },
      ]

  return (
    <div style={{ padding: '12px 16px', background: 'hsl(var(--muted))', borderBottom: '1px solid hsl(var(--border))' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          className="qa-input" value={text} placeholder={t('placeholder')}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) submit() }}
          style={{ borderLeft: `4px solid ${NOTE_COLORS[color].edge}` }}
        />
        <button type="button" title={t('changeColor')} onClick={cycleColor} className="qa-swatch" style={{
          background: NOTE_COLORS[color].bg, border: `2px solid ${NOTE_COLORS[color].deep}`,
        }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
        {presets.map(([label, iso]) => (
          <button key={iso} className={'qa-chip' + (date === iso && !picking ? ' on' : '')}
            onClick={() => { setDate(iso); setPicking(false) }}>{label}</button>
        ))}
        {picking ? (
          <input type="date" className="qa-date" value={date} min={todayISO(tz)}
            onChange={(e) => e.target.value && setDate(e.target.value)} />
        ) : (
          <button className={'qa-chip' + (!isPreset ? ' on' : '')} onClick={() => setPicking(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Calendar size={12} />{!isPreset ? shortDate(date) : t('pickDate')}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
        {timeChips.map((ch) => (
          <button key={ch.key} className={'qa-chip' + (ch.on ? ' on' : '')} onClick={ch.click}>{ch.label}</button>
        ))}
        {pickingTime ? (
          <input type="time" className="qa-time" value={time} autoFocus
            onChange={(e) => setTime(e.target.value)} title={t('pickTime')} />
        ) : (
          <button className={'qa-chip' + (time && !timeChips.some((ch) => ch.on) ? ' on' : '')}
            onClick={() => setPickingTime(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} />{time && !timeChips.some((ch) => ch.on) ? time : t('pickTime')}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={submit} disabled={!text.trim()} style={{
          height: 30, padding: '0 16px', borderRadius: 8, border: 'none', cursor: text.trim() ? 'pointer' : 'default',
          background: text.trim() ? 'hsl(var(--primary))' : 'hsl(var(--muted))', color: text.trim() ? 'hsl(var(--primary-foreground))' : 'hsl(var(--muted-foreground))', fontSize: 12.5, fontWeight: 700,
        }}>{t('add')}</button>
      </div>
    </div>
  )
}
